import {Injectable} from '@angular/core';

import {Observable} from 'rxjs/Observable';
import {User, UserCompany, UserRoleResponse, UserType} from '../../models/user';

import {permissionRules} from '../../permissions-rules';
import {NgxPermissionsService} from 'ngx-permissions';

import 'rxjs/add/operator/map';

import * as Sentry from '@sentry/browser';
import {Router} from '@angular/router';
import {environment} from '../../environments/environment';

import {AuthorizeService} from '../services/authorize.service';
import {UserService} from '../services/user.service';
import {ForgotPasswordService} from '../services/forgot-password.service';
import {ObjectUtils} from '../utils/object.utils';
import {Subject} from 'rxjs/Subject';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {FeaturePermission} from '../../models/influencer/feature-permission';
import {forkJoin} from 'rxjs/observable/forkJoin';
import {CommunicationService} from '../services/communication/communication.service';
import {InfluencerService} from '../services/influencer/influencer.service';
import {AppSettings} from '../utils/app-settings';
import {CommonConstant} from '../app.constants';
import {InfluencerChannelAuthorization} from '../../models/authorize-channel';
import {DateUtils} from '../utils/date.utils';
import {CacheService} from '../services/cache/cache.service';
import {catchError} from 'rxjs/operators';
import { WebUtils } from '../utils/web.utils';
import { CompanyStatus } from '../../models/common';

@Injectable()
export class AuthService {

    private user: User;
    private emailToResetPassword: string;
    private isAcceptTermsAndConditionSubject: Subject<boolean> = new BehaviorSubject<boolean>(true);
    private influencerFeaturePermissionsSubject: BehaviorSubject<FeaturePermission> = new BehaviorSubject<FeaturePermission>(null);
    private readonly CAMPAIGN_INVITATION_LINK_REG = new RegExp(/dashboard\/campaigns\/\d+\/new\?inviteToken=\w+/i);
    private readonly CONTACT_PERMISSION_REQUEST_LINK_REG = new RegExp(/dashboard\/contact-permission\?accessToken=\w+/i);

    constructor(
        private permissionsService: NgxPermissionsService,
        private authService: AuthorizeService,
        private userService: UserService,
        private forgotPasswordService: ForgotPasswordService,
        private communicationService: CommunicationService,
        private influencerService: InfluencerService,
        private cacheService: CacheService,
        private router: Router,
    ) {
        const currentUser = ObjectUtils.parseJSON(localStorage.getItem('user'));
        if (currentUser !== null && currentUser.token) {

            this.user = currentUser;

            this.permissionsService.flushPermissions();
            this.permissionsService.addPermission(this.user.permissions);
        }
    }

    public isAuth(): boolean {
      return ObjectUtils.parseJSON(localStorage.getItem('user')) !== null;
    }

    public getUser(): User {
        return ObjectUtils.parseJSON(localStorage.getItem('user')) as User || new User();
    }

    public getInfluencerChannelAuthorizationLocalData() {
      return ObjectUtils.parseJSON(localStorage.getItem(CommonConstant.CHANNEL_AUTHORIZATION_DATA)) as InfluencerChannelAuthorization;
    }

    public removeInfluencerChannelAuthorizationLocalData() {
      localStorage.removeItem(CommonConstant.CHANNEL_AUTHORIZATION_DATA);
    }

    public saveInfluencerChannelAuthorizationDataOnLocal(authChannelData: InfluencerChannelAuthorization) {
      localStorage.setItem(CommonConstant.CHANNEL_AUTHORIZATION_DATA, JSON.stringify(authChannelData));
    }

    public saveAuthorizedChannelOnLocal(authorizedChannelId) {
      let authChannels = ObjectUtils.parseJSON(sessionStorage.getItem(CommonConstant.CHANNEL_AUTHORIZATION_DATE));
      if (!authChannels) {
        authChannels = authorizedChannelId;
      } else {
        authChannels = `${authChannels},${authorizedChannelId}`;
      }
      sessionStorage.setItem(CommonConstant.CHANNEL_AUTHORIZATION_DATE, JSON.stringify(authChannels));
    }

    public removeIgChannelAuthorizationTimeOnLocal() {
      sessionStorage.removeItem(CommonConstant.CHANNEL_AUTHORIZATION_DATE);
    }

    public hasAuthorizedIgChannel(authorizedChannelId) {
      const authorizeChannels = sessionStorage.getItem(CommonConstant.CHANNEL_AUTHORIZATION_DATE);
      if (!authorizeChannels) {
        return false;
      }
      return authorizeChannels.indexOf(authorizedChannelId) >= 0;
    }


    public getPermissions(role: UserType) {
        const permissions = [];

        const type: UserType = role || UserType.GUEST;

        const permissionsKeys = Object.keys(permissionRules);
        for (const key of permissionsKeys) {
            if (permissionRules[key][type] === true) {
                permissions.push(key);
            }
        }

        return permissions;
    }

    public normalizeUserType(role, company?: UserCompany): UserType {
        switch (role) {
            case UserRoleResponse.ADMINISTRATOR: {
                return UserType.ADMIN;
            }

            case UserRoleResponse.ANALYST: {
                return UserType.ANALYST;
            }

            case UserRoleResponse.USER: {
                if (company) {
                    if (company.advertiser === true) {
                        return UserType.ADVERTISER;
                    }

                    if (company.influencer === true) {
                        return UserType.INFLUENCER;
                    }

                    if (company.network === true) {
                        return UserType.NETWORK;
                    }
                }

                return UserType.AGENCY;
            }

            default:
                return UserType.GUEST;
        }
    }

    private mapUserData(user) {
      if (!this.user) {
        this.user = new User();
      }
      this.user.data = user.data;
      this.user.email = user.data.email;
      this.user.type = this.normalizeUserType(user.data.role.name, user.data.company);
      this.user.companyId = user.data.company.id;
      this.user.companyName = user.data.company.name;
      this.user.fullName = user.data.fullName;
      this.user.createdAt = user.data.createdAt;
      this.user.permissions = this.getPermissions(this.user.type);
      this.permissionsService.addPermission(this.user.permissions);

      if (user.data.token) {
        this.user.token = user.data.token;
      }

      localStorage.setItem('user', JSON.stringify(this.user));
      this.userService.setIsSsoUser(user.data.isSsoUser);

      this.permissionsService.flushPermissions();
      this.permissionsService.addPermission(this.user.permissions);
    }

    getUserData(success?: (User) => void, failed?: (any) => void) {
      return this.userService.getCurrentUserInfo().subscribe(
        (user) => {
          // update user
          this.user.data = user.data;
          this.user.email = user.data.email;
          this.user.type = this.normalizeUserType(user.data.role.name, user.data.company);
          this.user.companyId = user.data.company.id;
          this.user.companyName = user.data.company.name;
          this.user.fullName = user.data.fullName;
          this.user.createdAt = user.data.createdAt;
          this.user.hasBilling = user.data.company.hasBilling;
          this.user.permissions = this.getPermissions(this.user.type);
          this.permissionsService.addPermission(this.user.permissions);

          Sentry.configureScope(scope => {
            scope.setUser({
              id: this.user.data.id,
              name: this.user.data.fullName,
              email: this.user.data.email,
              company: this.user.data.company.name,
              type: this.user.type
            });
          });

          localStorage.setItem('user', JSON.stringify(this.user));
          this.userService.setIsSsoUser(user.data.isSsoUser);

          this.permissionsService.flushPermissions();
          this.permissionsService.addPermission(this.user.permissions);

          if (success) {
            success(user);
          }
        }, (err) => {
          if (failed) {
            failed(err);
          }
        }
      );
    }

    forgotPassword(email: string, success?: (ForgotPasswordResponse) => void, failed?: (any) => void) {
      return this.forgotPasswordService.forgotPassword(email, environment.hostName).subscribe(success, failed);
    }

    login(username: string, password: string, success?: (User) => void, failed?: (any) => void) {
      return this.authService.authorizeUser(username, password).subscribe(
        (data) => {
          const token: string = data.Authorization;

          if (token) {
            this.saveAuthTokenInLocal(token, username);
            if (success) {
              success(data);
            }
          } else {
            return Observable.throw('Missing token');
          }
        }, (err) => {
          WebUtils.reportErrorToSentry('Login error', err);

          if (failed) {
            failed(err);
          }
        }
      );
    }

    saveAuthTokenInLocal(token, username) {
      if (!token) {
        return;
      }
      this.user = new User();
      this.user.email = username;
      this.user.token = token;
      localStorage.setItem('user', JSON.stringify(this.user));
    }

    loginByToken(token: string, activeAccount: number): Observable<boolean> {
      return this.userService.authorizeUserByToken(token).map(res => {
        if (!res) {
          return false;
        }
        this.mapUserData(res);
        if (!this.userService.getCompanyId()) {
          // this.userService.setActiveCompany([{id: activeAccount, name: 'PDF user'}]);
          const { company } = res.data;
          console.log(`company id: ${company.id} - name: ${company.name}`);
          this.userService.setActiveCompany(undefined, {
              id: activeAccount ? activeAccount : company.id,
              name: company ? company.name : 'PDF user',
              inactive: this.isCustomerStatusInactive(company.customerStatus)
          });
        }
        return true;
      });
    }

    loginByInviteToken(inviteToken: string, campaignId: string, activeAccount: number): Observable<boolean> {
      return this.userService.authorizeUserByInviteToken(inviteToken, campaignId).pipe(catchError((err) => {
        this.router.navigate(['access-denied']);
        return Observable.of(false);
      })).map(res => {
        if (!res) {
          return false;
        }
        this.mapUserData(res);
        if (!this.userService.getCompanyId()) {
          // this.userService.setActiveCompany([{id: activeAccount, name: 'PDF user'}]);
          const { company } = res.data;
          console.log(`company id: ${company.id} - name: ${company.name}`);
          this.userService.setActiveCompany(undefined, {
            id: activeAccount ? activeAccount : company.id,
            name: company ? company.name : 'PDF user',
            inactive: this.isCustomerStatusInactive(company.customerStatus)
          });
        }
        return true;
      });
    }

  /**
   * check if account is influencer or not
   * @param companyId
   * @returns {Observable<boolean>}
   */
    authInfluencerFromServer(companyId, url): Observable<boolean> {
      return this.userService.loadInfluencerInfoByCompanyId(companyId).map(res => {
        if (!res) {
          return this.isAccessingLandingPage(url);
        }
        this.userService.addInfluencerInfo(res);
        this.checkFeaturePermission(res.id, companyId);
        return true;
      });
    }

    private checkFeaturePermission(influencerId, companyId: number) {
      const observerRequests = forkJoin(
        this.checkConversationPermission(companyId),
        this.checkCampaignPermission(influencerId)
      );
      observerRequests.subscribe(([res1, res2]) => {
        const featurePermission = new FeaturePermission(res2, res1);
        this.influencerFeaturePermissionsSubject.next(featurePermission);
      });
    }

    private checkCampaignPermission(influencerId): Observable<boolean> {
      const data = {
        influencer_id: influencerId,
        page: 1,
        page_size: 10
      };
      if (AppSettings.isDisableCampaign()) {
        return Observable.of(false);
      }
      return this.influencerService.getCampaignListOfInfluencer(data).map(res => Array.isArray(res) && res.length > 0);
    }

    private checkConversationPermission(companyId): Observable<boolean> {
      if (AppSettings.isDisableChat()) {
        return Observable.of(false);
      }
      return this.communicationService.fetchConservationList(companyId, CommonConstant.DEFAULT_CONVERSATION_OFFSET, DateUtils.now())
        .map(res => Array.isArray(res.conversations) && res.conversations.length > 0);
    }

    logout(): void {
      this.resetUserData();

      const isSsoUser = this.userService.isSsoUser;
      this.userService.clearIsSsoUser();

      if (isSsoUser) {
        WebUtils.navigateToMmiLogoutPage();
        return;
      }

      this.router.navigate([`/login`]);
    }

    resetUserData(): void {

      Sentry.configureScope(scope => {
        scope.setUser({});
      });

      localStorage.removeItem('user');

      this.userService.resetActiveAccount();
      this.userService.clearAdvertiserData();
      this.userService.clearCampaignLastViewTime();
    }

    updateResetPassEmail(email: string) {
      this.emailToResetPassword = email;
    }

    get resetPasswordEmail() {
      return this.emailToResetPassword;
    }

    get termsAndConditionObserver(): Observable<boolean> {
      return this.isAcceptTermsAndConditionSubject.asObservable();
    }

    haveAcceptedTermAndCondition() {
      this.isAcceptTermsAndConditionSubject.next(true);
    }

    needAcceptedTermAndCondition() {
      this.isAcceptTermsAndConditionSubject.next(false);
    }

    updateUser(title) {
      const user = this.getUser();
      user.data.userTitle = title;
      localStorage.setItem('user', JSON.stringify(user));
    }

    get influencerFeaturePermission(): Observable<FeaturePermission> {
      return this.influencerFeaturePermissionsSubject.asObservable();
    }

    isEnableCampaign(): Observable<boolean> {
      const featurePermission = this.influencerFeaturePermissionsSubject.value;
      if (featurePermission) {
        return Observable.of(featurePermission.enableCampaign);
      }
      const currentInfluencer = this.userService.getInfluencerInfo();
      if (currentInfluencer) {
        return this.checkCampaignPermission(currentInfluencer.id);
      }
      return Observable.of(false);
    }

    public isAccessingLandingPage(url) {
      return this.CAMPAIGN_INVITATION_LINK_REG.test(url) || this.CONTACT_PERMISSION_REQUEST_LINK_REG.test(url);
    }

    private isCustomerStatusInactive(customerStatus: CompanyStatus): boolean {
      return customerStatus === CompanyStatus.BLOCKED
        || customerStatus === CompanyStatus.CHURNED;
    }
}



// WEBPACK FOOTER //
// ./src/app/core/auth.service.ts